// Members API client — Phase 4 Plan 04-01 Task 3 (D-78, DIS-04).
//
// Wraps:
//   GET    /api/v1/members                  (list members + invites)
//   POST   /api/v1/members/invite           (create dispatcher invite — owner-only)
//   POST   /api/v1/members/{id}/revoke      (revoke invite — owner-only)
//   DELETE /api/v1/members/{user_id}        (remove member — owner-only)
//
// All mutating endpoints require an Idempotency-Key header (server enforces
// via FastAPI's `Header(..., alias="Idempotency-Key")`); helper
// `generateIdempotencyKey()` is copied verbatim from customerInvites.ts
// per 04-PATTERNS §S-05 (jsdom-safe fallback for tests).

import type { ApiClient } from "./client";

export type TargetRole = "dispatcher" | "owner";
export type MemberRole = "owner" | "dispatcher" | "driver";

export interface MemberListItem {
  user_id: number;
  telegram_id: number;
  role: MemberRole;
  full_name: string | null;
  joined_at: string;
}

export interface CompanyInviteResponse {
  id: number;
  target_role: TargetRole;
  invited_by_user_id: number;
  expires_at: string;
  accepted_at: string | null;
  accepted_by_user_id: number | null;
  revoked_at: string | null;
  created_at: string;
  token_hash_prefix: string;
}

export interface CompanyInviteCreateOneTime {
  invite: CompanyInviteResponse;
  deep_link: string;
}

export interface MembersListResponse {
  members: MemberListItem[];
  invites: CompanyInviteResponse[];
}

export interface RevokeResponse {
  revoked: boolean;
  revoked_at: string;
}

export interface RemoveMemberResponse {
  removed_user_id: number;
  prior_role: MemberRole;
}

function generateIdempotencyKey(): string {
  // Browser crypto.randomUUID — available в all modern browsers + jsdom.
  // Fallback к timestamp + random pair if randomUUID not present.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const membersApi = {
  list(client: ApiClient): Promise<MembersListResponse> {
    return client.get<MembersListResponse>("/api/v1/members");
  },

  createInvite(
    client: ApiClient,
    targetRole: TargetRole = "dispatcher",
  ): Promise<CompanyInviteCreateOneTime> {
    return client.fetch<CompanyInviteCreateOneTime>("/api/v1/members/invite", {
      method: "POST",
      headers: { "Idempotency-Key": generateIdempotencyKey() },
      body: JSON.stringify({ target_role: targetRole }),
    });
  },

  revokeInvite(client: ApiClient, inviteId: number): Promise<RevokeResponse> {
    return client.fetch<RevokeResponse>(`/api/v1/members/${inviteId}/revoke`, {
      method: "POST",
      headers: { "Idempotency-Key": generateIdempotencyKey() },
      body: JSON.stringify({}),
    });
  },

  removeMember(client: ApiClient, userId: number): Promise<RemoveMemberResponse> {
    return client.fetch<RemoveMemberResponse>(`/api/v1/members/${userId}`, {
      method: "DELETE",
    });
  },
};
